import { type AbilitiesManager } from '@canvas/abilities/abilities-manager';
import { getAbilitiesManager } from '@canvas/abilities/abilities-manager/singleton';

import { baseMode } from './modes/base-mode';
import { type ApplicationModeIdentifier, type ApplicationMode } from './types';

/**
 * Responsible for maintaining the current mode the application is running in,
 * and orchestrating safe context-switches between modes.
 */
export class ApplicationModeManager {
  /**
   * Current mode the application is running in. Is locked to the current
   * session. Any changes made to it will yield a new session to avoid leakage.
   */
  #mode: ApplicationMode;

  /**
   * Current abilities a user has, encapsulated into a consolidated interface.
   */
  #abilities: AbilitiesManager;

  static create(mode = baseMode, abilitiesManager = getAbilitiesManager()) {
    const manager = new ApplicationModeManager(mode, abilitiesManager);

    // apply policies of initial mode
    abilitiesManager.policies.add(manager.mode(), manager.capabilities().policies);

    return manager;
  }

  private constructor(mode: ApplicationMode, abilitiesManager: AbilitiesManager) {
    this.#mode = mode;
    this.#abilities = abilitiesManager;
  }

  /**
   * Switch from mode A -> mode B.
   *
   * @remark Will transition policies from the previous mode to the next mode.
   * @remark Avoid switching modes once the application has already started. If
   * a change in mode takes place, it will destroy the application from the
   * session down to the renderer to avoid any leakage between modes.
   * @remark Run logic which should react to a change in mode AFTER this method.
   *
   * @param mode target mode to switch to
   */
  transitionTo(mode: ApplicationMode) {
    // not allowed to transition from this mode - early exit
    if (this.#mode.type === 'persistent') {
      return;
    }

    // already in requested mode - nothing to do
    if (this.#mode.id === mode.id) {
      return;
    }

    // configure policies correctly on transition first
    this.#abilities.policies.remove(this.#mode.id);
    this.#abilities.policies.add(mode.id, mode.policies);

    // update active mode
    this.#mode = mode;
  }

  /**
   * Current mode the application is in.
   *
   * @remark The current mode can change. See {@link transitionTo} for more.
   *
   * @returns
   */
  mode(): ApplicationModeIdentifier {
    return this.#mode.id;
  }

  /**
   * Current capabilities the application has, based upon the active mode.
   *
   * @remark It is possible for the mode to change. When it does, the
   * application will be thrown away & recreated, all the way from the session
   * down to the renderer. This is deliberate, to avoid leakage between modes.
   *
   * @returns current capabilities of the application
   */
  capabilities(): ApplicationMode {
    return this.#mode;
  }
}

export const applicationModeManager = ApplicationModeManager.create();
